import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";

const Interactive3DSection = () => {
  const cardsRef = useRef([]);

  const movementIntensity = 40;

  const handleMouseMove = (e, index) => {
    const card = cardsRef.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * movementIntensity;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * movementIntensity;

    card.style.transform = `rotateX(${-y}deg) rotateY(${x}deg) scale(1.06)`;
  };

  const handleMouseLeave = (index) => {
    const card = cardsRef.current[index];
    if (!card) return;
    card.style.transform = "rotateX(0deg) rotateY(0deg) scale(1)";
  };

  const cards = [
    {
      title: "Instagram",
      image: "https://celebratufiesta.com.co/wp-content/uploads/2025/01/instagram-1.png",
      link: "https://www.instagram.com/ernesto.moras/",
      iconImage: "https://img.icons8.com/ios-filled/50/ffffff/instagram-new.png",
      alt: "Instagram de Ernesto Mora — IA aplicada en SST y seguridad laboral",
    },
    {
      title: "YouTube",
      image: "https://celebratufiesta.com.co/wp-content/uploads/2025/01/Youtube-1.png",
      link: "https://youtube.com",
      iconImage: "https://img.icons8.com/ios-filled/50/ffffff/youtube-play.png",
      alt: "YouTube de Ernesto Mora — formación en SST e inteligencia artificial",
    },
    {
      title: "TikTok",
      image: "https://celebratufiesta.com.co/wp-content/uploads/2025/01/Tiktok-1.png",
      link: "https://www.tiktok.com/@businesstipsbyernesto",
      iconImage: "https://img.icons8.com/ios-filled/50/ffffff/tiktok.png",
      alt: "TikTok de Ernesto Mora — tips rápidos de SST e IA",
    },
    {
      title: "LinkedIn",
      image: "https://celebratufiesta.com.co/wp-content/uploads/2025/01/Linkdn-1.png",
      link: "https://www.linkedin.com/in/ernestomorasoto/",
      iconImage: "https://img.icons8.com/ios-filled/50/ffffff/linkedin.png",
      alt: "LinkedIn de Ernesto Mora — consultoría y liderazgo HSE en LATAM",
    },
  ];

  // JSON-LD para vincular perfiles sociales (mejora de entidad de marca personal)
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Ernesto Mora",
    "jobTitle": "Speaker y Consultor en IA aplicada en SST",
    "url": "https://ernestomora.com/",
    "sameAs": cards.map((c) => c.link)
  };

  return (
    <section
      id="social"
      className="py-16 bg-black text-white"
      aria-label="Redes sociales de Ernesto Mora sobre IA aplicada en SST"
    >
      {/* Solo deja <Helmet> si esta sección es una ruta propia. Si está en Home, puedes omitir title. */}
      <Helmet>
        <title>Redes Sociales | IA aplicada en SST — Ernesto Mora</title>
        <meta
          name="description"
          content="Sigue a Ernesto Mora en Instagram, YouTube, TikTok y LinkedIn para aprender sobre IA aplicada en SST, prevención de riesgos laborales y productividad HSE."
        />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 text-center">
        {/* H2 con keyword y gradiente correcto (Tailwind) */}
        <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-[#FFD700] via-[#FFC700] to-[#FFB800] bg-clip-text text-transparent">
          Sígueme en redes: IA aplicada en SST
        </h2>
        {/* Microcopy con variaciones semánticas -> ayuda al SEO */}
        <p className="text-gray-300 mb-10 max-w-3xl mx-auto">
          Contenido diario sobre <strong>Seguridad y Salud en el Trabajo</strong>,
          <strong> inteligencia artificial aplicada</strong>, automatización de informes,
          inspecciones inteligentes y casos reales para profesionales HSEQ en Latinoamérica.
        </p>

        {/* Lista semántica */}
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card, index) => (
            <li key={card.title} className="flex flex-col items-center">
              {/* Mejor SEO/UX: el contenedor es un <a> rastreable */}
              <a
                href={card.link}
                target="_blank"
                rel="noopener noreferrer"
                ref={(el) => (cardsRef.current[index] = el)}
                className="relative group rounded-lg shadow-lg overflow-hidden transition-transform duration-300 cursor-pointer block focus:outline-none focus:ring-2 focus:ring-yellow-400"
                onMouseMove={(e) => handleMouseMove(e, index)}
                onMouseLeave={() => handleMouseLeave(index)}
                aria-label={`Abrir ${card.title} de Ernesto Mora en nueva pestaña`}
                title={`${card.title} de Ernesto Mora`}
              >
                <img
                  src={card.image}
                  alt={card.alt}
                  className="h-96 w-64 object-cover rounded-lg"
                  loading="lazy"
                  decoding="async"
                  width="256"
                  height="384"
                  onError={(e) => {
                    e.currentTarget.style.objectFit = "contain";
                    e.currentTarget.src =
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        '<svg xmlns="http://www.w3.org/2000/svg" width="256" height="384"><rect width="100%" height="100%" fill="black"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="14">Imagen no disponible</text></svg>'
                      );
                  }}
                />
              </a>

              {/* Icono y texto abajo (anchor con texto descriptivo) */}
              <a
                href={card.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 hover:underline"
                aria-label={`Seguir en ${card.title}`}
                title={`Seguir en ${card.title}`}
              >
                <img
                  src={card.iconImage}
                  alt={`Icono de ${card.title}`}
                  className="h-7 w-7"
                  loading="lazy"
                  width="28"
                  height="28"
                />
                <span className="text-sm text-gray-200">
                  {card.title} — Ernesto Mora
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Interactive3DSection;
